import React from 'react';
import { Link } from 'react-router-dom';
import {
  Store,
  Truck,
  Smartphone,
  ArrowRight,
  Zap,
  BarChart3,
  Tablet,
  LifeBuoy,
  CheckCircle2,
  Globe
} from 'lucide-react';
import TurboLogo from '../components/TurboLogo';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LandingPage: React.FC = () => {
  const { t, language, dir } = useLanguage();

  return (
    <div className="min-h-screen font-sans text-gray-900 bg-white" dir={dir}>
      {/* Header */}
      <header className="fixed w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
              <TurboLogo width={42} height={36} color="#DC2626" />
              <span className="text-2xl font-bold tracking-tight text-gray-900">
                {t('turboPlatform')}
              </span>
            </div>

            {/* Navigation */}
            <div className={`hidden md:flex items-center ${language === 'ar' ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
              <a href="#features" className="text-sm font-medium text-gray-600 hover:text-red-600 transition-colors">
                {t('whyChooseTurboPlatform')}
              </a>
              <a href="#solutions" className="text-sm font-medium text-gray-600 hover:text-red-600 transition-colors">
                {t('threePowerfulApplications')}
              </a>
            </div>

            {/* CTA Buttons */}
            <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
              <Link
                to="/restaurant/login"
                className="hidden sm:inline-flex text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {t('restaurantLogin')}
              </Link>
              <Link
                to="/restaurant/register"
                className="bg-red-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-red-700 transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {t('getStarted')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden lg:pt-40 lg:pb-28">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-red-50 via-white to-white opacity-70"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium text-red-600 bg-red-50 border border-red-100 mb-8">
              <span className={`flex items-center ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <span className="bg-red-600 w-2 h-2 rounded-full mr-2 ml-1"></span>
                {t('builtForModernBusinesses')}
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-gray-900 mb-8 leading-tight">
              {t('theComplete')}
              <span className="block mt-2 bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                {t('foodDeliveryPlatform')}
              </span>
            </h1>

            <p className="mt-6 text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              {t('streamlineDescription')}
            </p>

            <div className={`flex flex-col sm:flex-row gap-4 justify-center ${language === 'ar' ? 'sm:flex-row-reverse' : ''}`}>
              <Link
                to="/restaurant/register"
                className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-bold rounded-full text-lg hover:bg-red-700 transition-all duration-200 shadow-xl hover:shadow-2xl hover:-translate-y-1"
              >
                {t('startYourRestaurant')}
                <ArrowRight className={`${language === 'ar' ? 'mr-2 rotate-180' : 'ml-2'} h-5 w-5`} />
              </Link>
              <Link
                to="/restaurant/login"
                className="inline-flex items-center justify-center px-8 py-4 border border-gray-200 text-gray-700 font-bold rounded-full text-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 bg-white shadow-sm"
              >
                {t('restaurantLogin')}
              </Link>
            </div>
          </div>
        </div>

        {/* Abstract shapes/decoration */}
        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-64 h-64 bg-red-100/50 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-100/50 rounded-full blur-3xl -z-10"></div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              {t('threePowerfulApplications')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('seamlessSolutions')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Restaurant Portal */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-150 duration-500"></div>
              <div className="relative">
                <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-red-200 group-hover:scale-110 transition-transform duration-300">
                  <Store className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('restaurantPortal')}</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('completeRestaurantManagement')}
                </p>
                <ul className="space-y-4 mb-8">
                  {[
                    t('advancedPOSDashboard'),
                    t('tableQROrderPlacement'),
                    t('revenuePerformanceReports')
                  ].map((item, i) => (
                    <li key={i} className={`flex items-start ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle2 className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className={`text-gray-700 ${language === 'ar' ? 'mr-3' : 'ml-3'}`}>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/restaurant/register"
                  className="inline-flex items-center text-red-600 font-semibold hover:text-red-700 transition-colors group-hover:translate-x-1"
                >
                  {t('getStarted')}
                  <ArrowRight className={`${language === 'ar' ? 'mr-2 rotate-180' : 'ml-2'} h-4 w-4`} />
                </Link>
              </div>
            </div>

            {/* Driver App */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-150 duration-500"></div>
              <div className="relative">
                <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-green-200 group-hover:scale-110 transition-transform duration-300">
                  <Truck className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('turboDriverApp')}</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('mobileApplicationForDrivers')}
                </p>
                <ul className="space-y-4 mb-8">
                  {[
                    t('acceptManageOrders'),
                    t('realTimeNavigation'),
                    t('earningsTracking')
                  ].map((item, i) => (
                    <li key={i} className={`flex items-start ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className={`text-gray-700 ${language === 'ar' ? 'mr-3' : 'ml-3'}`}>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-3">
                  <a href="#" className="opacity-80 hover:opacity-100 transition-opacity">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className="h-10" />
                  </a>
                  <a href="#" className="opacity-80 hover:opacity-100 transition-opacity">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" className="h-10" />
                  </a>
                </div>
              </div>
            </div>

            {/* Delivery App */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-150 duration-500"></div>
              <div className="relative">
                <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-purple-200 group-hover:scale-110 transition-transform duration-300">
                  <Smartphone className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('turboDeliveryApp')}</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('customerFacingMobileApp')}
                </p>
                <ul className="space-y-4 mb-8">
                  {[
                    t('browseRestaurantMenus'),
                    t('liveDeliveryTracking'),
                    t('seamlessUserExperience')
                  ].map((item, i) => (
                    <li key={i} className={`flex items-start ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle2 className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                      <span className={`text-gray-700 ${language === 'ar' ? 'mr-3' : 'ml-3'}`}>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-3">
                  <a href="#" className="opacity-80 hover:opacity-100 transition-opacity">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className="h-10" />
                  </a>
                  <a href="#" className="opacity-80 hover:opacity-100 transition-opacity">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" className="h-10" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('whyChooseTurboPlatform')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('builtForModernBusinesses')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Zap, color: 'text-yellow-500', bg: 'bg-yellow-50', title: t('lightningFast'), desc: t('realTimeOrderProcessing') },
              { icon: BarChart3, color: 'text-blue-500', bg: 'bg-blue-50', title: t('smartAnalytics'), desc: t('dataDrivenInsights') },
              { icon: Tablet, color: 'text-purple-500', bg: 'bg-purple-50', title: t('crossPlatform'), desc: t('worksSeamlessly') },
              { icon: LifeBuoy, color: 'text-green-500', bg: 'bg-green-50', title: t('support247'), desc: t('expertAssistance') }
            ].map((feature, i) => (
              <div key={i} className="text-center p-6 rounded-2xl hover:bg-gray-50 transition-colors duration-300">
                <div className={`w-16 h-16 ${feature.bg} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <feature.icon className={`w-8 h-8 ${feature.color}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
            {/* Brand */}
            <div>
              <div className={`flex items-center mb-6 ${language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                <TurboLogo width={36} height={30} color="#FFFFFF" />
                <span className="text-2xl font-bold tracking-tight">{t('turboPlatform')}</span>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('completeFoodDeliveryPlatform')}
              </p>

              {/* Language Switcher in Footer */}
              <div className="flex items-center text-gray-400 hover:text-white transition-colors">
                <LanguageSwitcher />
              </div>
            </div>

            {/* Links Columns */}
            <div>
              <h3 className="text-lg font-bold mb-6 text-white">{t('platform')}</h3>
              <ul className="space-y-4 text-gray-400">
                <li><Link to="/restaurant/register" className="hover:text-white transition-colors">{t('restaurantPortal')}</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('driverApp')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('deliveryApp')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-6 text-white">{t('support')}</h3>
              <ul className="space-y-4 text-gray-400">
                <li><Link to="/restaurant/login" className="hover:text-white transition-colors">{t('restaurantLogin')}</Link></li>
                <li><Link to="/admin" className="hover:text-white transition-colors">{t('adminAccess')}</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('helpCenter')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-6 text-white">{t('connect')}</h3>
              <ul className="space-y-4 text-gray-400">
                <li><Link to="/contact" className="hover:text-white transition-colors">{t('contactUs')}</Link></li>
                <li className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500 uppercase">Email</span>
                  <a href="mailto:support@turbo.sd" className="hover:text-white transition-colors">support@turbo.sd</a>
                </li>
                <li className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500 uppercase">Phone</span>
                  <a href="tel:+249100633522" className="hover:text-white transition-colors" dir="ltr">+249 100 633 522</a>
                  <a href="tel:+249909103353" className="hover:text-white transition-colors" dir="ltr">+249 909 103 353</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              {t('allRightsReserved')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
